import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlinePrinter } from 'react-icons/ai';
import { FiChevronRight, FiFileText } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import {
  DataTable,
  StyledButton,
  StyledDateInput,
  StyledSelect,
  SummarizedByFlagDataTable,
} from '../../../../../components';
import { ExportButton } from '../../../../../components/ExportButton';
import { useFilters } from '../../../../../services/contexts/FiltersContext';
import { useUserDocument } from '../../../../../services/contexts/UserDocumentContext';
import { useRelatorioPagamento } from '../../../../../services/hooks/useRelatorioPagamento';
import { PrintComponent } from '../../../../../services/PrintComponent';
import { CnpjMask } from '../../../../../utils/CnpjMask';
import { CpfMask } from '../../../../../utils/CpfMask';
import { AdminTitle } from '../../../components';
import { Container, FiltersContainer } from './styles';
import { InputDescription } from '../../../components/InputDescription';

const tableHead = [
  'Guia',
  'Documento',
  'Nome Fantasia',
  'Parcela',
  'Emissão',
  'Vencimento',
  'Produto',
  'Vl. Bruto',
  'Tx. Adm',
  'Tx. Antec.',
  'Descontos',
  'Vl. Líquido',
  'Vl. Pago',
  'Dt. Pgto',
  'Vl. Aberto',
  'Status',
  'Consulta',
];

export const RelatorioPagamento = () => {
  const { documentSelected } = useUserDocument();
  const docto = useMemo(() => documentSelected as string, [documentSelected]);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [print, setPrint] = useState(false);

  const {
    periodType,
    setPeriodType,
    initialDate,
    setInitialDate,
    finalDate,
    setFinalDate,
    situation,
    setSituation,
    productType,
    setProductType,
    flag,
    setFlag,
  } = useFilters();

  const [params, setParams] = useState({
    docto: '',
    dtInicial: initialDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    dtFinal: finalDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    pagina: page,
    limite: itemsPerPage,
    tipoPeriodo: periodType,
    situacao: situation,
    bandeira: flag,
    tipoProd: productType,
  });

  useEffect(() => {
    if (docto) {
      setParams((previousState) => ({ ...previousState, docto: docto }));
    }
  }, [docto]);

  const { data, isLoading, isFetching, error, refetch } = useRelatorioPagamento(
    { ...params, pagina: page, limite: itemsPerPage }
  );

  /**
   * Alguns títulos da API vem com dois espaços em brancos entre cada palavra
   * ou com um espaço em branco no começo da string. Essa função serve para tratar isso.
   *
   * @param title título que será tratado.
   * @returns retorna o título tratado.
   */
  const convertTitle = (title: string | undefined) => {
    if (!title) {
      return null;
    }

    let newTitle = title;

    if (newTitle[0] === ' ') {
      newTitle = title.slice(1);
    }

    if (newTitle.includes('  ')) {
      newTitle = newTitle.replace('  ', ' ');
    }

    return newTitle;
  };

  /**
   * Aplica o convertTitle nos dados retornados pela api
   *
   * @returns retorna os mesmos dados da API, mas com o produtoDescrição
   * tratado com a função "convertTitle"
   */
  const transactionWithProdutoDescricaoConverted = data?.transactions?.map(
    (transaction) => ({
      ...transaction,
      produtoDescricao: convertTitle(transaction.produtoDescricao),
    })
  );

  /**
   * A API retorna a bandeira do cartão + forma de pagamento em uma única chave.
   * Essa constante serve para remover a bandeira do cartão e retorna as formas de pagamento
   * de todas transações;
   */
  const paymentMethods = [
    ...new Set(
      transactionWithProdutoDescricaoConverted?.map((transaction) => {
        const isPix =
          transaction.produtoDescricao === 'PIX'
            ? transaction.produtoDescricao
            : null;

        return (
          isPix || transaction.produtoDescricao?.split(' ').slice(1).join(' ')
        );
      })
    ),
  ];

  /**
   * Essa função irá pegar as formas de pagamento e fará um filtro dentro dos dados retornados
   * da API, reunindo as transações com a mesma forma de pagamento dentro de um array.
   * Por fim, irá aplicar um new Set para remover os arrays duplicados.
   *
   * @returns irá retornar um array de arrays, onde cada array irá possuir transações
   * com a mesma forma de pagamento.
   */
  const processedData = [...new Set(paymentMethods)]?.map((paymentMethod) =>
    transactionWithProdutoDescricaoConverted?.filter(
      (transaction) =>
        paymentMethod && transaction.produtoDescricao?.includes(paymentMethod)
    )
  );

  const summarizedByFlagData = processedData?.map(
    (arrayByPaymentMethod, index) => ({
      paymentMethod: paymentMethods?.[index],
      totalLiquido: arrayByPaymentMethod?.reduce(
        (acc, transaction) => (acc += transaction.vlLiquido),
        0
      ),
      totalBruto: arrayByPaymentMethod?.reduce(
        (acc, transaction) => (acc += transaction.vlParcela),
        0
      ),
      data: arrayByPaymentMethod?.reduce((acc, transaction) => {
        if (typeof acc[transaction.bandeira] === 'undefined') {
          acc[transaction.bandeira] = 0;
        }
        acc[transaction.bandeira] += transaction.vlParcela;
        return acc;
      }, {} as any),
    })
  );

  const handleFilter = useCallback(() => {
    const newParams = {
      docto: docto,
      dtInicial: initialDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      dtFinal: finalDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      pagina: page,
      limite: itemsPerPage,
      tipoPeriodo: periodType,
      situacao: situation,
      bandeira: flag,
      tipoProd: productType,
    };

    setParams(newParams);
    setPage(1);
    setItemsPerPage(10);
    refetch();
  }, [
    docto,
    initialDate,
    finalDate,
    page,
    itemsPerPage,
    periodType,
    situation,
    flag,
    productType,
    refetch,
  ]);

  return (
    <Container>
      <AdminTitle title="Relatório Movimentação" icon={<FiFileText />} />
      <h3>Consulte aqui suas movimentações</h3>

      <FiltersContainer>
        <div>
          <InputDescription placeholder="Filtro por período" />
          <StyledSelect
            placeholder="Tipo de Período"
            options={[
              { id: 'E', value: 'Emissão' },
              { id: 'V', value: 'Vencimento' },
              { id: 'P', value: 'Pagamento' },
            ]}
            setValue={setPeriodType}
            value={periodType}
          />
        </div>

        <div>
          <InputDescription placeholder="Data inicial" />
          <StyledDateInput
            value={initialDate}
            onChange={setInitialDate}
            placeholder="Data inicial"
          />
        </div>

        <div>
          <InputDescription placeholder="Data final" />
          <StyledDateInput
            value={finalDate}
            onChange={setFinalDate}
            placeholder="Data final"
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por situação" />
          <StyledSelect
            placeholder="Situação"
            options={[
              { id: 'A', value: 'Em aberto' },
              { id: 'B', value: 'Baixado' },
              { id: 'T', value: 'Todos' },
            ]}
            setValue={setSituation}
            value={situation}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por produto" />
          <StyledSelect
            placeholder="Tipo de Produto"
            options={[
              { id: 'DB', value: 'Débito' },
              { id: 'CV', value: 'Crédito à Vista' },
              { id: 'CP', value: 'Crédito Parcelado' },
              { id: 'PX', value: 'Pix' },
            ]}
            setValue={setProductType}
            value={productType}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por bandeira" />
          <StyledSelect
            placeholder="Bandeira"
            options={[
              { id: 'AMEX', value: 'Amex' },
              { id: 'CABAL', value: 'Cabal' },
              { id: 'ELO', value: 'Elo' },
              { id: 'HIPERCARD', value: 'Hipercard' },
              { id: 'MASTERCARD', value: 'Mastercard' },
              { id: 'VISA', value: 'Visa' },
            ]}
            setValue={setFlag}
            value={flag}
          />
        </div>

        <StyledButton
          text="Consultar"
          icon={<FiChevronRight />}
          iconPosition="right"
          onClick={handleFilter}
          iconWhiteBackground
          iconBorderRadius
        />

        <ExportButton
          params={params}
          transactionType="ordemRepasse"
          disabled={isLoading || isFetching}
        />

        <StyledButton
          text="Imprimir"
          icon={<AiOutlinePrinter />}
          iconPosition="right"
          onClick={() => setPrint(true)}
          disabled={isLoading || isFetching}
        />
      </FiltersContainer>
      <PrintComponent onPrint={print} setOnPrint={setPrint}>
        <DataTable
          title="Detalhado"
          heads={tableHead}
          hasPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          totalItems={data?.total}
        >
          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={18}>Carregando...</td>
              </tr>
            </tbody>
          ) : error ? (
            <tbody>
              <tr>
                <td colSpan={18}>Falha ao obter dados</td>
              </tr>
            </tbody>
          ) : data?.transactions.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={18}>Não foram encontrados dados</td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {data?.transactions?.map((pagamento) => (
                <tr key={pagamento.idOrdemRepasse}>
                  <td className="itemClicavel">
                    <Link
                      to={`/admin/relatorio-movimentacao/${
                        pagamento.idOrdemRepasse
                      }/${JSON.stringify(pagamento)}`}
                      target="_blank"
                    >
                      {`${pagamento.numOrdem + pagamento.serie}`}
                    </Link>
                  </td>
                  <td>
                    {pagamento.estabDocto?.length === 14
                      ? CnpjMask(pagamento.estabDocto)
                      : CpfMask(pagamento.estabDocto)}
                  </td>
                  <td>{pagamento.estabFantasia}</td>
                  <td>{pagamento.parcela}</td>
                  <td>
                    {new Date(pagamento.dtMovimento).toLocaleDateString(
                      'pt-BR'
                    )}
                  </td>
                  <td>
                    {new Date(pagamento.dtPrevPgto).toLocaleDateString('pt-BR')}
                  </td>
                  <td>{pagamento.produtoDescricao}</td>
                  <td>
                    {pagamento.vlParcela.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.vlTxAdm.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.vlTxAnt.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.vlOutrosDesc.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.vlLiquido.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.vlPgto.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.dtPgto
                      ? new Date(pagamento.dtPgto).toLocaleDateString('pt-BR')
                      : '-'}
                  </td>
                  <td>
                    {pagamento.vlAberto.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {pagamento.status === 1
                      ? 'Em processamento'
                      : 'Processamento concluído'}
                  </td>
                  <td className="itemClicavel">
                    <Link
                      to={`/admin/baixas/${pagamento.idOrdemRepasse}`}
                      target="_blank"
                    >
                      Baixas
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </DataTable>

        <SummarizedByFlagDataTable
          summarizedByFlagData={summarizedByFlagData}
        />
      </PrintComponent>
    </Container>
  );
};
